import { Dialog, Transition, TransitionChild } from "@headlessui/react";
import { X, ChevronLeft, ChevronRight, Copy, ExternalLink, ImageOff, Download, Loader2 } from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import { isCryptoWalletCurrency } from "@/modules/dashboard/confirmations/constants/currencies.config";
import { confirmationsService } from "@/modules/dashboard/confirmations/services/confirmations.service";
import type { ConfirmationByIdPayload } from "@/modules/dashboard/confirmations/types/confirmations-api.types";
import {
  NOTE_CATEGORY_LABELS,
  NOTE_PRIORITY_LABELS,
  NOTE_STATUS_LABELS,
} from "@/modules/dashboard/news/constants/news.constants";
import { notesService } from "@/modules/dashboard/news/services/notes.service";
import type { NoteByIdPayload } from "@/modules/dashboard/news/types/notes-api.types";
import { FORM_METHOD_FIELD_LABEL_TKEYS } from "@/modules/dashboard/payments/constants/form-method-field-translations";
import { paymentsService } from "@/modules/dashboard/payments/services/payments.service";
import type { PaymentByIdPayload } from "@/modules/dashboard/payments/types/payments-api.types";
import type { AuditTranslations } from "@/shared/lang/interfaces/audit";
import type { PaymentsTranslations } from "@/shared/lang/interfaces/payments";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import type { AuditoriaRow } from "../types/auditoria.types";

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: "Pendiente",
  IN_PROGRESS: "En proceso",
  PAID: "Pagado",
  CANCELLED: "Cancelado",
  ARCHIVED: "Archivado",
  FILED: "Archivado",
};

const CONFIRMATION_STATUS_LABELS: Record<string, string> = {
  PENDING: "Pendiente",
  CONFIRMED: "Confirmado",
  CANCELLED: "Cancelado",
  ARCHIVED: "Archivado",
  NOTIFIED: "Notificado",
};

function getStatusBadgeClass(status: string): string {
  const s = status.toUpperCase();
  if (["CONFIRMED", "PAID", "NOTIFIED", "ACTIVE"].includes(s))
    return "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-500/30";
  if (["PENDING", "IN_PROGRESS"].includes(s))
    return "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/20 dark:text-amber-200 dark:border-amber-500/30";
  if (["CANCELLED"].includes(s))
    return "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-500/20 dark:text-rose-200 dark:border-rose-500/30";
  return "bg-slate-50 text-slate-700 border-slate-100 dark:bg-slate-500/20 dark:text-slate-200 dark:border-slate-500/30";
}

function getStatusDotClass(status: string): string {
  const s = status.toUpperCase();
  if (["CONFIRMED", "PAID", "NOTIFIED", "ACTIVE"].includes(s)) return "bg-emerald-500";
  if (["PENDING", "IN_PROGRESS"].includes(s)) return "bg-amber-500";
  if (["CANCELLED"].includes(s)) return "bg-rose-500";
  return "bg-slate-400";
}

function formatAmount(value: number | string | null | undefined): string {
  if (value == null) return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function getCurrencySymbol(currency: string): string {
  const map: Record<string, string> = {
    COP: "$",
    USD: "$",
    EUR: "€",
    BRL: "R$",
    MXN: "$",
    USDT: "₮",
    USDC: "$",
  };
  return map[currency] ?? currency.slice(0, 1);
}

function isAuditoriaStablecoinCurrency(currency: unknown): boolean {
  if (currency == null) return false;
  return isCryptoWalletCurrency(String(currency));
}

function formatBankMetadataKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^\s+/, "")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function getBankMetadataFieldLabel(key: string, t: (k: keyof PaymentsTranslations) => string): string {
  const translationKey = FORM_METHOD_FIELD_LABEL_TKEYS[key.trim()];
  if (translationKey) return t(translationKey);
  return formatBankMetadataKey(key);
}

function formatBankMetadataValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Sí" : "No";
  return JSON.stringify(value);
}

function getImageLabel(index: number, comprobanteIndex: number | undefined): string | null {
  if (comprobanteIndex === undefined) return null;
  if (comprobanteIndex === -1) return `Anexo ${index + 1}`;
  if (index === comprobanteIndex) return "Comprobante";
  let anexoNum = 0;
  for (let j = 0; j <= index; j++) if (j !== comprobanteIndex) anexoNum++;
  return `Anexo ${anexoNum}`;
}

type EntityPayload = ConfirmationByIdPayload | PaymentByIdPayload;

function getStatusLabel(payload: EntityPayload): string {
  const s = payload.status;
  if (s in CONFIRMATION_STATUS_LABELS) return CONFIRMATION_STATUS_LABELS[s] ?? s;
  return PAYMENT_STATUS_LABELS[s] ?? s;
}

type DetailField =
  | { type: "text"; label: string; value: string }
  | { type: "currency"; label: string; value: string }
  | { type: "status"; label: string; value: string; statusRaw: string }
  | { type: "notes"; label: string; value: string };

function hasValue(v: unknown): v is NonNullable<unknown> {
  return v != null && v !== undefined;
}

function buildDetailFields(payload: EntityPayload): DetailField[] {
  const fields: DetailField[] = [];

  if (hasValue(payload.reference_code)) {
    fields.push({
      type: "text",
      label: "Código de Referencia",
      value: String(payload.reference_code),
    });
  }
  if (hasValue(payload.currency)) {
    fields.push({ type: "currency", label: "Moneda", value: payload.currency });
  }
  if (hasValue(payload.client_name)) {
    fields.push({
      type: "text",
      label: "Cliente",
      value: String(payload.client_name),
    });
  }
  if (hasValue(payload.payer_name)) {
    fields.push({
      type: "text",
      label: "Pagador",
      value: String(payload.payer_name),
    });
  }
  if (hasValue(payload.amount)) {
    fields.push({
      type: "text",
      label: "Monto",
      value: formatAmount(payload.amount),
    });
  }
  if (hasValue(payload.bank)) {
    fields.push({ type: "text", label: "Banco", value: String(payload.bank) });
  }
  if (hasValue(payload.payment_date)) {
    fields.push({
      type: "text",
      label: "Fecha de Pago",
      value: formatDateTime(payload.payment_date, { includeTime: false }),
    });
  }
  if (hasValue(payload.status)) {
    fields.push({
      type: "status",
      label: "Estado",
      value: getStatusLabel(payload),
      statusRaw: payload.status,
    });
  }
  const notes = (payload as { notes?: string | null }).notes;
  if (hasValue(notes) && String(notes).trim()) {
    fields.push({ type: "notes", label: "Notas", value: String(notes).trim() });
  }

  return fields;
}

export type AuditoriaArchivosModalProps = {
  isOpen: boolean;
  onClose: () => void;
  row: AuditoriaRow | null;
  alt?: string;
};

export function AuditoriaArchivosModal({
  isOpen,
  onClose,
  row,
  alt = "Comprobante",
}: AuditoriaArchivosModalProps) {
  const { t: paymentsT } = useTranslations("payments");
  const { t: auditT } = useTranslations("audit") as {
    t: (k: keyof AuditTranslations) => string;
  };
  const imageUrls = row?.archivosUrls ?? [];
  const comprobanteIndex = row?.comprobanteIndex ?? -1;
  const entityType = row?.entityType;
  const entityId = row?.entityId ?? "";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [entityData, setEntityData] = useState<EntityPayload | NoteByIdPayload | null>(null);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entityError, setEntityError] = useState<string | null>(null);

  const count = imageUrls.length;
  const currentUrl = count > 0 ? (imageUrls[currentIndex] ?? null) : null;
  const hasPrev = count > 1 && currentIndex > 0;
  const hasNext = count > 1 && currentIndex < count - 1;
  const currentLabel = getImageLabel(currentIndex, comprobanteIndex);

  const canFetchEntity =
    entityType === "PAYMENT_CONFIRMATION" || entityType === "PAYMENT" || entityType === "NOTE";

  useEffect(() => {
    if (!isOpen) {
      setEntityData(null);
      setEntityError(null);
      return;
    }
    if (row) {
      setCurrentIndex(Math.min(Math.max(0, 0), Math.max(0, imageUrls.length - 1)));
      setLoadError(false);
    }
  }, [isOpen, row, imageUrls.length]);

  useEffect(() => {
    if (!isOpen || !entityId || !canFetchEntity) {
      setEntityData(null);
      setEntityError(null);
      return;
    }
    let cancelled = false;
    setEntityLoading(true);
    setEntityError(null);
    setEntityData(null);

    const fetchEntity = async () => {
      try {
        if (entityType === "PAYMENT_CONFIRMATION") {
          const data = await confirmationsService.fetchById(entityId);
          if (!cancelled) setEntityData(data);
        } else if (entityType === "PAYMENT") {
          const data = await paymentsService.fetchById(entityId);
          if (!cancelled) setEntityData(data);
        } else if (entityType === "NOTE") {
          const data = await notesService.fetchById(entityId);
          if (!cancelled) setEntityData(data);
        }
      } catch {
        if (!cancelled) setEntityError("No se pudo cargar la información.");
      } finally {
        if (!cancelled) setEntityLoading(false);
      }
    };

    void fetchEntity();
    return () => {
      cancelled = true;
    };
  }, [isOpen, entityId, entityType, canFetchEntity]);

  const goPrev = useCallback(() => {
    if (hasPrev) {
      setCurrentIndex((i) => i - 1);
      setLoadError(false);
    }
  }, [hasPrev]);

  const goNext = useCallback(() => {
    if (hasNext) {
      setCurrentIndex((i) => i + 1);
      setLoadError(false);
    }
  }, [hasNext]);

  const openInNewTab = useCallback(() => {
    if (currentUrl) {
      const link = document.createElement("a");
      link.href = currentUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    }
  }, [currentUrl]);

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadComprobante = useCallback(async () => {
    if (!currentUrl) return;
    setIsDownloading(true);
    try {
      const res = await fetch(currentUrl);
      const blob = await res.blob();
      const ext = currentUrl.split(/[#?]/)[0].match(/\.(jpe?g|png|gif|webp|pdf)$/i)?.[1] ?? "jpg";
      const filename = count > 1 ? `comprobante-${currentIndex + 1}.${ext}` : `comprobante.${ext}`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  }, [currentUrl, count, currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goPrev, goNext]);

  const detailFields =
    entityData && (entityType === "PAYMENT" || entityType === "PAYMENT_CONFIRMATION")
      ? buildDetailFields(entityData as EntityPayload)
      : [];

  const bankMetadataEntries =
    entityType === "PAYMENT" &&
    entityData &&
    "bank_metadata" in entityData &&
    entityData.bank_metadata != null &&
    typeof entityData.bank_metadata === "object"
      ? Object.entries(entityData.bank_metadata as Record<string, unknown>).filter(
          ([, v]) => v !== undefined && v !== null && v !== "",
        )
      : [];

  const hasBankMetadataEntries = bankMetadataEntries.length > 0;

  const paymentOrderedFields =
    entityType === "PAYMENT" && detailFields.length > 0
      ? (() => {
          const refField = detailFields.find((f) => f.label === "Código de Referencia");
          const statusField = detailFields.find((f) => f.type === "status");
          const clientField = detailFields.find((f) => f.label === "Cliente");
          const payerField = detailFields.find((f) => f.label === "Pagador");
          const notesField = detailFields.find((f) => f.type === "notes");
          const restFields = detailFields.filter(
            (f) =>
              f !== refField &&
              f !== statusField &&
              f !== clientField &&
              f !== payerField &&
              f !== notesField &&
              f.label !== "Fecha de Pago" &&
              !(hasBankMetadataEntries && f.label === "Banco"),
          );
          return [refField, statusField, clientField, payerField, ...restFields, notesField].filter(
            Boolean,
          ) as typeof detailFields;
        })()
      : detailFields;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-slate-900 flex items-center justify-center min-h-screen p-4"
            aria-hidden
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto p-4 flex items-center justify-center min-h-screen">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="bg-white dark:bg-zinc-900 w-full max-w-6xl rounded-4xl shadow-2xl flex flex-col md:flex-row overflow-hidden min-h-[700px] transform"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Left: Document viewer */}
              <section className="flex-1 bg-slate-100 dark:bg-zinc-800/50 p-6 flex flex-col gap-4 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold tracking-widest text-slate-500 dark:text-slate-400 uppercase">
                      {currentLabel ?? "Comprobante"}
                    </span>
                    <div className="flex items-center bg-slate-200 dark:bg-zinc-700 rounded-lg overflow-hidden border border-slate-300 dark:border-zinc-600">
                      <button
                        type="button"
                        onClick={goPrev}
                        disabled={!hasPrev}
                        className="px-3 py-1.5 bg-white dark:bg-zinc-600 hover:bg-slate-50 dark:hover:bg-zinc-500 border-r border-slate-300 dark:border-zinc-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Anterior"
                      >
                        <ChevronLeft className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                      </button>
                      <span className="px-3 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
                        {count > 0 ? `${currentIndex + 1} / ${count}` : "0 / 0"}
                      </span>
                      <button
                        type="button"
                        onClick={goNext}
                        disabled={!hasNext}
                        className="px-3 py-1.5 bg-white dark:bg-zinc-600 hover:bg-slate-50 dark:hover:bg-zinc-500 border-l border-slate-300 dark:border-zinc-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Siguiente"
                      >
                        <ChevronRight className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={downloadComprobante}
                      disabled={!currentUrl || isDownloading}
                      className="flex items-center gap-2 bg-white dark:bg-zinc-700 px-4 py-2 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-zinc-600 hover:bg-slate-50 dark:hover:bg-zinc-600 transition-all shadow-sm disabled:opacity-50"
                    >
                      <Download className="h-4 w-4" />
                      Descargar
                    </button>
                    <button
                      type="button"
                      onClick={openInNewTab}
                      disabled={!currentUrl}
                      className="flex items-center gap-2 bg-white dark:bg-zinc-700 px-4 py-2 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-zinc-600 hover:bg-slate-50 dark:hover:bg-zinc-600 transition-all shadow-sm disabled:opacity-50"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Nueva pestaña
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-[320px] max-h-[80vh] bg-slate-500 dark:bg-zinc-700 rounded-2xl overflow-hidden relative flex items-center justify-center border-4 border-slate-300 dark:border-zinc-600 shrink-0">
                  {count === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-12 text-slate-400">
                      <ImageOff className="h-12 w-12 opacity-60" />
                      <p className="text-sm">
                        {entityType === "NOTE"
                          ? auditT("modal_archivos_no_comprobantes_note")
                          : entityType === "PAYMENT" &&
                              (row?.accion === "creacion" ||
                                row?.accion === "cambio_estado" ||
                                row?.accion === "actualizacion_datos" ||
                                row?.accion === "eliminacion")
                            ? auditT("modal_archivos_no_comprobantes_payment_creation")
                            : auditT("modal_archivos_no_comprobantes_default")}
                      </p>
                    </div>
                  ) : currentUrl && !loadError ? (
                    <img
                      src={currentUrl}
                      alt={`${alt} ${currentIndex + 1}`}
                      className="max-w-full max-h-full w-auto h-auto object-contain opacity-90"
                      draggable={false}
                      onError={() => setLoadError(true)}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-3 py-12 text-slate-400">
                      <ImageOff className="h-12 w-12 opacity-60" />
                      <p className="text-sm text-center px-4">
                        No se pudo cargar este comprobante. Use &quot;Nueva pestaña&quot; para intentar.
                      </p>
                    </div>
                  )}
                  {count > 1 && count <= 10 && (
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                      {imageUrls.map((url, index) => (
                        <button
                          key={`${url}-${index}`}
                          type="button"
                          onClick={() => {
                            setCurrentIndex(index);
                            setLoadError(false);
                          }}
                          className={cn(
                            "w-12 h-12 rounded-lg border-2 overflow-hidden shadow-lg bg-white dark:bg-zinc-600 p-1 transition-opacity",
                            index === currentIndex
                              ? "border-emerald-500 dark:border-emerald-400"
                              : "border-white/50 dark:border-zinc-500/50 opacity-70 hover:opacity-100 cursor-pointer",
                          )}
                        >
                          <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {/* Right: Registration details */}
              <aside className="w-full md:w-96 lg:w-[28rem] p-6 flex flex-col border-l border-slate-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 min-w-0">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                    Información del registro
                  </h2>
                  <button
                    type="button"
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-400 transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-5 flex-1 overflow-y-auto pr-2 min-h-0 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb]:rounded-full dark:[&::-webkit-scrollbar-track]:bg-zinc-800 dark:[&::-webkit-scrollbar-thumb]:bg-zinc-600">
                  {!canFetchEntity && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      No hay información adicional para este flujo.
                    </p>
                  )}
                  {canFetchEntity && entityLoading && (
                    <div className="flex flex-col items-center justify-center py-8 gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                      <p className="text-sm text-slate-500 dark:text-slate-400">Cargando…</p>
                    </div>
                  )}
                  {canFetchEntity && entityError && (
                    <p className="text-sm text-rose-600 dark:text-rose-400">{entityError}</p>
                  )}
                  {canFetchEntity &&
                    !entityLoading &&
                    !entityError &&
                    entityData &&
                    (entityType === "NOTE" ? (
                      (() => {
                        const note = entityData as NoteByIdPayload;
                        return (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                            {hasValue(note.title) && (
                              <div className="flex flex-col gap-1 col-span-2">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  Título
                                </label>
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                                  {String(note.title)}
                                </p>
                              </div>
                            )}
                            {hasValue(note.category) && (
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  Categoría
                                </label>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border w-fit bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600">
                                  {NOTE_CATEGORY_LABELS[note.category as keyof typeof NOTE_CATEGORY_LABELS] ??
                                    note.category}
                                </span>
                              </div>
                            )}
                            {hasValue(note.priority) && (
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  Prioridad
                                </label>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border w-fit bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600">
                                  {NOTE_PRIORITY_LABELS[note.priority as keyof typeof NOTE_PRIORITY_LABELS] ??
                                    note.priority}
                                </span>
                              </div>
                            )}
                            {hasValue(note.status) && (
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  Estado
                                </label>
                                <span
                                  className={cn(
                                    "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border w-fit",
                                    getStatusBadgeClass(note.status),
                                  )}
                                >
                                  <span
                                    className={cn(
                                      "w-2 h-2 rounded-full mr-2 shrink-0",
                                      getStatusDotClass(note.status),
                                    )}
                                  />
                                  {NOTE_STATUS_LABELS[note.status as keyof typeof NOTE_STATUS_LABELS] ??
                                    note.status}
                                </span>
                              </div>
                            )}
                            {note.is_pinned != null && (
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  Fijada
                                </label>
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                  {note.is_pinned ? "Sí" : "No"}
                                </p>
                              </div>
                            )}
                            {note.user != null && (
                              <div className="flex flex-col gap-1 col-span-2">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  Usuario
                                </label>
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                                  {note.user.full_name ?? note.user.email ?? "—"}
                                </p>
                              </div>
                            )}
                            {hasValue(note.created_at) && (
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  Creado
                                </label>
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                  {formatDateTime(note.created_at)}
                                </p>
                              </div>
                            )}
                            {hasValue(note.content) && (
                              <div className="flex flex-col gap-1 col-span-2 mt-1">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  Contenido
                                </label>
                                <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700">
                                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed wrap-break-word whitespace-pre-wrap">
                                    {String(note.content).trim()}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : entityType === "PAYMENT_CONFIRMATION" && entityData ? (
                      (() => {
                        const payload = entityData as EntityPayload;
                        return (
                          <>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                              {hasValue(payload.reference_code) && (
                                <div className="flex flex-col gap-1">
                                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                    Código de Referencia
                                  </label>
                                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                                    {String(payload.reference_code)}
                                  </p>
                                </div>
                              )}
                              {hasValue(payload.status) && (
                                <div className="flex flex-col gap-1">
                                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                    Estado
                                  </label>
                                  <span
                                    className={cn(
                                      "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border w-fit",
                                      getStatusBadgeClass(payload.status),
                                    )}
                                  >
                                    <span
                                      className={cn(
                                        "w-2 h-2 rounded-full mr-2 shrink-0",
                                        getStatusDotClass(payload.status),
                                      )}
                                    />
                                    {getStatusLabel(payload)}
                                  </span>
                                </div>
                              )}
                            </div>
                            {hasValue((payload as ConfirmationByIdPayload).confirmation_source) &&
                              String((payload as ConfirmationByIdPayload).confirmation_source).trim() && (
                                <div className="flex flex-col gap-1 mt-5">
                                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                    {auditT("modal_archivos_confirmation_source_label")}
                                  </label>
                                  <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700 shadow-sm">
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                                      {String(
                                        (payload as ConfirmationByIdPayload).confirmation_source,
                                      ).trim()}
                                    </p>
                                  </div>
                                </div>
                              )}
                            {hasValue((payload as { notes?: string | null }).notes) &&
                              String((payload as { notes?: string | null }).notes).trim() && (
                                <div className="flex flex-col gap-1 mt-5">
                                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                    Notas
                                  </label>
                                  <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700">
                                    <p className="text-sm text-slate-600 dark:text-slate-300 italic leading-relaxed wrap-break-word">
                                      {String((payload as { notes?: string | null }).notes).trim()}
                                    </p>
                                  </div>
                                </div>
                              )}
                            {(payload as ConfirmationByIdPayload).deletion_note?.trim() && (
                              <div className="flex flex-col gap-1 mt-5">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  {auditT("modal_archivos_deletion_note_label")}
                                </label>
                                <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700">
                                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed wrap-break-word whitespace-pre-wrap">
                                    {String((payload as ConfirmationByIdPayload).deletion_note).trim()}
                                  </p>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()
                    ) : detailFields.length > 0 ? (
                      <>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                          {paymentOrderedFields
                            .filter((f) => f.type !== "notes")
                            .map((field, idx) => (
                              <div key={idx} className="flex flex-col gap-1 min-w-0">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  {field.label === "Pagador" ? "Beneficiario" : field.label}
                                </label>
                                {field.type === "text" && (
                                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                                    {field.value}
                                  </p>
                                )}
                                {field.type === "currency" && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-emerald-100 dark:bg-emerald-500/30 rounded-full flex items-center justify-center shrink-0">
                                      <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-300">
                                        {field.value ? getCurrencySymbol(field.value) : "—"}
                                      </span>
                                    </div>
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                      {field.value || "—"}
                                    </p>
                                  </div>
                                )}
                                {field.type === "status" && (
                                  <span
                                    className={cn(
                                      "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border w-fit",
                                      getStatusBadgeClass(field.statusRaw),
                                    )}
                                  >
                                    <span
                                      className={cn(
                                        "w-2 h-2 rounded-full mr-2 shrink-0",
                                        getStatusDotClass(field.statusRaw),
                                      )}
                                    />
                                    {field.value}
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                        {entityType === "PAYMENT" &&
                          hasValue((entityData as PaymentByIdPayload).payment_source) &&
                          String((entityData as PaymentByIdPayload).payment_source).trim() && (
                            <div className="flex flex-col gap-1 mt-5">
                              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                {auditT("modal_archivos_payment_source_label")}
                              </label>
                              <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700 shadow-sm">
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                                  {String((entityData as PaymentByIdPayload).payment_source).trim()}
                                </p>
                              </div>
                            </div>
                          )}
                        {entityType === "PAYMENT" &&
                          (hasBankMetadataEntries ||
                            (entityData &&
                              "currency" in entityData &&
                              isAuditoriaStablecoinCurrency((entityData as PaymentByIdPayload).currency) &&
                              (entityData as PaymentByIdPayload).bank != null &&
                              String((entityData as PaymentByIdPayload).bank).trim() !== "")) && (
                            <div className="mt-5">
                              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500 block mb-1.5">
                                Información de la cuenta
                              </label>
                              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                                {entityData &&
                                  "currency" in entityData &&
                                  isAuditoriaStablecoinCurrency(
                                    (entityData as PaymentByIdPayload).currency,
                                  ) &&
                                  (entityData as PaymentByIdPayload).bank != null &&
                                  String((entityData as PaymentByIdPayload).bank).trim() !== "" && (
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                      <dt className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                        {paymentsT("card_network_label")}
                                      </dt>
                                      <dd className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                                        {String((entityData as PaymentByIdPayload).bank).trim()}
                                      </dd>
                                    </div>
                                  )}
                                {bankMetadataEntries.map(([key, value]) => (
                                  <div key={key} className="flex flex-col gap-0.5 min-w-0">
                                    <dt className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                      {getBankMetadataFieldLabel(key, paymentsT)}
                                    </dt>
                                    <dd className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word">
                                      {formatBankMetadataValue(value)}
                                    </dd>
                                  </div>
                                ))}
                              </dl>
                            </div>
                          )}
                        {entityType === "PAYMENT" &&
                          entityData &&
                          isAuditoriaStablecoinCurrency((entityData as PaymentByIdPayload).currency) &&
                          hasValue((entityData as PaymentByIdPayload).url_has) &&
                          String((entityData as PaymentByIdPayload).url_has).trim() !== "" &&
                          (() => {
                            const urlHashValue = String((entityData as PaymentByIdPayload).url_has).trim();
                            return (
                              <div className="flex flex-col gap-1 mt-5">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                  url_hash de confirmación
                                </label>
                                <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700 shadow-sm">
                                  <div className="flex items-start gap-2">
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 wrap-break-word break-all flex-1 min-w-0">
                                      {urlHashValue}
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        void navigator.clipboard
                                          .writeText(urlHashValue)
                                          .then(() => colocaToast.success(paymentsT("card_copied")))
                                          .catch(() => {});
                                      }}
                                      className="flex shrink-0 items-center justify-center w-9 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-zinc-700 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                                      title={paymentsT("card_copy_title")}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        {(() => {
                          const notesField = paymentOrderedFields.find((f) => f.type === "notes");
                          return notesField ? (
                            <div className="flex flex-col gap-1 mt-5">
                              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                                Notas
                              </label>
                              <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700">
                                <p className="text-sm text-slate-600 dark:text-slate-300 italic leading-relaxed wrap-break-word">
                                  {notesField.value}
                                </p>
                              </div>
                            </div>
                          ) : null;
                        })()}
                        {(entityData as PaymentByIdPayload)?.deletion_note?.trim() && (
                          <div className="flex flex-col gap-1 mt-5">
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                              {auditT("modal_archivos_deletion_note_label")}
                            </label>
                            <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700">
                              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed wrap-break-word whitespace-pre-wrap">
                                {String((entityData as PaymentByIdPayload).deletion_note).trim()}
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : null)}
                  {row &&
                    (row.flujo === "pago" || row.flujo === "confirmacion") &&
                    row.accion === "eliminacion" &&
                    row.deletion_note?.trim() &&
                    !entityData && (
                      <div className="flex flex-col gap-1 mt-5">
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                          {auditT("modal_archivos_deletion_note_label")}
                        </label>
                        <div className="bg-slate-50 dark:bg-zinc-800/50 p-4 rounded-xl border border-slate-100 dark:border-zinc-700">
                          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed wrap-break-word whitespace-pre-wrap">
                            {row.deletion_note.trim()}
                          </p>
                        </div>
                      </div>
                    )}
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 dark:border-zinc-800 shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-semibold py-3 px-4 rounded-xl transition-all shadow-lg active:scale-[0.98]"
                  >
                    Cerrar Detalle
                  </button>
                </div>
              </aside>
            </Dialog.Panel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
